import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-[#0F172A] py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white font-serif">
            Terms of Service
          </h1>
          <p className="text-white/70 mt-4">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Agreement to Terms</h2>
            <p className="text-[#475569] leading-relaxed">
              By accessing and using the Next Chapter Moves NC website (nextchaptermovesnc.net), you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please do not use our website.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Services Description</h2>
            <p className="text-[#475569] leading-relaxed">
              Next Chapter Moves NC provides real estate guidance and information services for homeowners in North Carolina, particularly those who are downsizing, relocating, or planning future housing transitions. Our website offers educational resources, guides, and the ability to contact us for personalized assistance.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Use of Website</h2>
            <p className="text-[#475569] leading-relaxed mb-4">
              You agree to use this website only for lawful purposes and in a way that does not:
            </p>
            <ul className="list-disc pl-6 text-[#475569] space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Transmit any harmful code or malware</li>
              <li>Collect information about other users without consent</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Intellectual Property</h2>
            <p className="text-[#475569] leading-relaxed">
              All content on this website, including text, graphics, logos, images, and the Next Chapter Guide, is the property of Next Chapter Moves NC and is protected by copyright laws. You may download and print materials for personal, non-commercial use only. You may not reproduce, distribute, or create derivative works without our written permission.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Disclaimer</h2>
            <p className="text-[#475569] leading-relaxed">
              The information provided on this website is for general informational purposes only and should not be considered as professional real estate, legal, financial, or tax advice. While we strive to provide accurate and up-to-date information, we make no warranties about the completeness, reliability, or accuracy of this information. Any reliance you place on such information is at your own risk.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Real Estate Services</h2>
            <p className="text-[#475569] leading-relaxed">
              Next Chapter Moves NC operates as a real estate service provider in North Carolina. Any real estate transactions are subject to separate agreements and are governed by applicable North Carolina real estate laws and regulations. The information on this website does not constitute an offer to buy or sell real estate.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Limitation of Liability</h2>
            <p className="text-[#475569] leading-relaxed">
              To the fullest extent permitted by law, Next Chapter Moves NC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of this website or our services. Our total liability shall not exceed the amount you paid to us, if any, in the twelve months preceding the claim.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Third-Party Links</h2>
            <p className="text-[#475569] leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the content, privacy practices, or terms of service of these external sites. We encourage you to review the terms and privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Modifications to Terms</h2>
            <p className="text-[#475569] leading-relaxed">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to this page. Your continued use of the website after any changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Governing Law</h2>
            <p className="text-[#475569] leading-relaxed">
              These Terms of Service shall be governed by and construed in accordance with the laws of the State of North Carolina, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Contact Us</h2>
            <p className="text-[#475569] leading-relaxed">
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <div className="mt-4 p-6 bg-white border-l-4 border-[#D4AF37]">
              <p className="text-[#0F172A] font-semibold">Next Chapter Moves NC</p>
              <p className="text-[#475569]">Knightdale, North Carolina</p>
              <p className="text-[#475569]">Email: <a href="mailto:info@nextchaptermovesnc.net" className="text-[#D4AF37] hover:underline">info@nextchaptermovesnc.net</a></p>
              <p className="text-[#475569]">Phone: <a href="tel:919-879-5636" className="text-[#D4AF37] hover:underline">(919) 879-5636</a></p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#0F172A] py-8 px-6 text-center">
        <p className="text-[#94A3B8] text-sm">
          © {new Date().getFullYear()} Next Chapter Moves NC. All rights reserved.
        </p>
      </div>
    </div>
  );
}
